import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";

import { Button, FormControl, MenuItem, Select, Stack, TextField } from "@mui/material";

import { api } from "../../../../../utils";
import { IError } from "../../../../../models/ReportPageModels";
import BackupUsersAutoComplete from "../../../controls/BackupUsersAutoComplete";

interface BackupUsersResponse {
    data: BackupUsersData;
    error: IError;
}
interface BackupUsersData {
    backupUsers: BackupUsersModel;
}
interface BackupUsersModel {
    desktop: string[];
    mobile: string[];
}

interface UserBackupsResponse {
    data: UserBackupsData;
    error: IError;
}
interface UserBackupsData {
    timeFolders: string[];
}

interface BackupContentResponse {
    data: BackupContentData;
    error: IError;
}
interface BackupContentData {
    content: string;
}

const storageKey: string = "backupViewer";
const backupTypes: string[] = ["Desktop", "Mobile"];

function BackupViewer() {
    const { enqueueSnackbar } = useSnackbar();

    const [showPassword, setShowPassword] = useState<boolean>(true);
    const [password, setPassword] = useState<string>("");

    const [selectedType, setSelectedType] = useState<"" | "Desktop" | "Mobile">("");

    const backupUsers = useRef<BackupUsersModel | null>(null);

    const [users, setUsers] = useState<string[]>([]);
    const [selectedUser, setSelectedUser] = useState<string>("");

    const [backups, setBackups] = useState<string[]>([]);
    const [selectedBackup, setSelectedBackup] = useState<string>("");

    const [backupContent, setBackupContent] = useState<string>("");

    const [typesDisabled, setTypesDisabled] = useState<boolean>(true);
    const [usersDisabled, setUsersDisabled] = useState<boolean>(true);
    const [backupsDisabled, setBackupsDisabled] = useState<boolean>(true);

    useEffect(() => {
        CheckTodayPassword();

        GetBackupUsers();
    }, []);

    function CheckTodayPassword() {
        var password = localStorage.getItem(storageKey);
        if (password === null)
            return;

        var correctPassword = CreatePassword();
        if (password === correctPassword)
            setShowPassword(false)
    }

    const GetBackupUsers = async () => {
        try {
            const response: BackupUsersResponse = await api.get("api/Admin/GetBackupUsers", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                backupUsers.current = response.data.backupUsers;
                setTypesDisabled(false);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function SelectType(event: any) {
        var value: "Desktop" | "Mobile" = event.target.value;
        setSelectedType(value);
        setSelectedUser("");

        setBackups([]);
        setSelectedBackup("");

        setBackupContent("");

        if (backupUsers.current == null) {
            enqueueSnackbar("An error occured. Please contact developers", { variant: "error" });
            return;
        }

        if (value === "Desktop") {
            setUsers(backupUsers.current!.desktop ?? []);
        }
        else {
            setUsers(backupUsers.current!.mobile ?? []);
        }

        setUsersDisabled(false);
    }

    function SelectUser(user: string) {
        setSelectedUser(user);
        setSelectedBackup("");
        setBackupContent("");
        GetUserBackups(user);
    }

    const GetUserBackups = async (user: string) => {
        setBackups([]);
        try {
            const response: UserBackupsResponse = await api.get(`api/Admin/GetUserBackups?type=${selectedType}&user=${user}`, {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setBackups(response.data.timeFolders);
                setBackupsDisabled(false);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function SelectBackup(event: any) {
        var value = event.target.value;
        setSelectedBackup(value);
        GetBackupContent(value);
    }

    const GetBackupContent = async (timeFolder: string) => {
        setBackupContent("");
        try {
            const response: BackupContentResponse = await api.get(`api/Admin/GetBackupContent?type=${selectedType}&user=${selectedUser}&folder=${timeFolder}`, {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setBackupContent(response.data.content);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const DownloadBackup = async (decrypt: boolean) => {
        if (selectedType === "" || selectedUser === "" || selectedBackup === "") {
            enqueueSnackbar("Please recheck selected values", { variant: "error" });
            return;
        }

        try {
            const response = await api.get(`api/Admin/GetBackupFile?type=${selectedType}&user=${selectedUser}&folder=${selectedBackup}&decrypt=${decrypt}`, {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).blob();

            if (response !== null) {
                //window.open(response.data.url, "_blank", "noreferrer");

                const link = document.createElement('a');
                link.href = URL.createObjectURL(response);
                link.setAttribute(
                    'download',
                    `${selectedUser}-${selectedBackup}-config.json`,
                );
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);
            }
            else {
                enqueueSnackbar("An error occured", { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function GetAreaHeight() {
        return window.innerHeight - 135;
    }

    const enterKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.code === "Enter")
            CheckPassword()
    }

    function CheckPassword() {
        var correctPassword = CreatePassword();
        if (password === correctPassword) {
            setShowPassword(false)
            // save password for today
            localStorage.setItem(storageKey, password);
        }
        else {
            enqueueSnackbar("Password is incorrect!", { variant: "error" });
        }
    }

    function CreatePassword(): string {
        var date = dayjs();
        var monthSymbol = String.fromCharCode(date.month() + 65); // 'A' has an ASCII value of 65. // month() return value is 0 - 11
        return `CI${monthSymbol}${date.format("DD")}`;
    }

    return (
        <>
            {showPassword ?
                <Stack>
                    <Stack
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center">
                        <span style={{ marginRight: "10px" }}>Enter password:</span>
                        <TextField
                            value={password}
                            onChange={(newValue: any) => { setPassword(newValue.target.value) }}
                            sx={{ minWidth: "245px" }}
                            size="small"
                            id="enterBackupPassword"
                            onKeyDown={enterKeyDownHandler} />
                    </Stack>

                    <Button
                        variant="outlined"
                        onClick={CheckPassword}
                        sx={{ width: "100px" }}>
                        Check
                    </Button>
                </Stack>
                :
                <Stack sx={{ marginLeft: "-10px", marginBottom: "-20px", marginRight: "-20px" }}>
                    <Stack
                        direction={"row"}
                        justifyContent="flex-start"
                        alignItems="center">
                        <span style={{ marginRight: "10px" }}>Type:</span>
                        <FormControl sx={{ minWidth: "150px" }}>
                            <Select
                                disabled={typesDisabled}
                                value={selectedType}
                                onChange={SelectType}
                                size="small">
                                {backupTypes.map((item, index) =>
                                    <MenuItem
                                        key={index}
                                        value={item}>
                                        {item}
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>

                        <span style={{ margin: "0 10px" }}>User:</span>
                        <BackupUsersAutoComplete
                            disabled={usersDisabled}
                            users={users}
                            selectUser={SelectUser}
                        />

                        <span style={{ margin: "0 10px" }}>Backups:</span>
                        <FormControl sx={{ minWidth: "200px" }}>
                            <Select
                                disabled={backupsDisabled}
                                value={selectedBackup}
                                onChange={SelectBackup}
                                size="small">
                                {backups.map((item, index) =>
                                    <MenuItem
                                        key={index}
                                        value={item}>
                                        {item}
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>

                        <Button
                            variant="outlined"
                            onClick={() => DownloadBackup(false)}
                            sx={{ marginLeft: "10px", height: "40px" }}>
                            Download
                        </Button>

                        {selectedType === "Desktop" ?
                            <Button
                                variant="outlined"
                                onClick={() => DownloadBackup(true)}
                                sx={{ marginLeft: "10px", height: "40px" }}>
                                Download decrypted
                            </Button>
                            :
                            null
                        }
                    </Stack>

                    <textarea
                        style={{ fontSize: "16px", height: GetAreaHeight(), width: "100%" }}
                        value={backupContent}
                        readOnly />
                </Stack>
            }
        </>
    )
}

export default BackupViewer;